// SupportChatService — estado global (module-level singleton) de la burbuja de chat de soporte
// (Reemplazo de Intercom, Fase 3). La UI (T2-B) se suscribe para abrir/cerrar el panel y para
// consumir el texto de prefill. Mantiene además los drafts en localStorage.
//
// Este service NO renderiza nada: es la capa de coordinación entre los call sites (que piden
// "abrí el chat con este mensaje") y el panel de UI.

import {LocalStorage} from '../../utils/LocalStorage';

// Si cambia, cambia también `SUPPORT_CHAT_DRAFT_PREFIX` en `utils/LocalStorage.js`, que los barre al
// cerrar la sesión.
const DRAFT_KEY_PREFIX = 'support_chat_draft_';
const CONVERSATION_URL_PARAM = 'support_conversation';

// `?support_conversation=<id>` es el link del mail «Te respondimos / Tienes un mensaje»: abre el
// chat en esa conversación. Se lee al CARGAR el módulo (lo importa App.js, antes de cualquier
// render) porque para cuando la burbuja monta, una redirección del router ya pudo haber borrado la
// query de la URL. Y se SACA de la URL al leerlo (con `replaceState`, conservando el resto de la
// query): si quedara, cada F5 volvería a abrir el chat.
function conversationFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get(CONVERSATION_URL_PARAM);
        if (raw === null) return null;
        params.delete(CONVERSATION_URL_PARAM);
        const query = params.toString();
        window.history.replaceState(window.history.state, '',
            window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
        const id = parseInt(raw, 10);
        return Number.isInteger(id) && id > 0 ? {id, status: null} : null;
    } catch (_) {
        return null;
    }
}

// Estado privado del módulo.
let _enabled = false;      // ¿el user está en la allowlist de soporte? (lo setea SupportChatEntry)
let _panelOpen = false;    // ¿el panel está abierto?
let _prefillText = null;   // texto a pre-cargar en el composer la próxima vez que abra el panel.
// Conversación a abrir la próxima vez que abra el panel: {id, status} (status null = desconocido).
let _pendingConversation = conversationFromUrl();

const _listeners = new Set();

function _notify(event) {
    // Cada listener recibe { type: 'open' | 'close' | 'prefill' | 'conversation' }.
    _listeners.forEach((listener) => {
        try {
            listener(event);
        } catch (_) {
            // Un listener roto no debe tumbar a los demás.
        }
    });
}

export const SupportChatService = {
    // ── Enable / gating ──────────────────────────────────────────────────────
    // Lo setea SupportChatEntry al montar, con support_chat_enabled de get-logged-user-data.
    setEnabled(value) {
        _enabled = !!value;
    },

    isEnabled() {
        return _enabled;
    },

    // ── Pub/sub del panel ────────────────────────────────────────────────────
    subscribe(listener) {
        _listeners.add(listener);
        return () => {
            _listeners.delete(listener);
        };
    },

    openPanel() {
        _panelOpen = true;
        _notify({type: 'open'});
    },

    closePanel() {
        _panelOpen = false;
        _notify({type: 'close'});
    },

    isPanelOpen() {
        return _panelOpen;
    },

    // Guarda el texto de prefill y abre el panel. La UI lo consume UNA vez con consumePrefill().
    openWithMessage(text) {
        _prefillText = text !== undefined && text !== null ? text : '';
        _panelOpen = true;
        _notify({type: 'prefill'});
        _notify({type: 'open'});
    },

    // Devuelve el prefill pendiente (o null) y lo limpia — se consume una sola vez.
    consumePrefill() {
        const text = _prefillText;
        _prefillText = null;
        return text;
    },

    // Abre el panel directo en una conversación («Responder» de la tarjeta de un mensaje del equipo,
    // o el link del mail). Si el panel ya está abierto, lo lleva a esa conversación. `status`
    // ('OPEN' | 'CLOSED') siembra el hilo hasta que su GET lo confirma: una respuesta a una
    // conversación cerrada tiene que mostrarse cerrada desde el primer render.
    openConversation(conversationId, status = null) {
        _pendingConversation = {id: conversationId, status};
        _panelOpen = true;
        _notify({type: 'conversation', id: conversationId});
        _notify({type: 'open'});
    },

    hasPendingConversation() {
        return _pendingConversation !== null;
    },

    // Devuelve la conversación pendiente ({id, status} o null) y la limpia — se consume una sola vez.
    consumePendingConversation() {
        const pending = _pendingConversation;
        _pendingConversation = null;
        return pending;
    },

    // Al salir de la sesión (logout SPA): nada de lo pendiente le corresponde al usuario siguiente.
    resetSession() {
        _pendingConversation = null;
        _prefillText = null;
        _panelOpen = false;
        _enabled = false;
    },

    // ── Reemplazo de los CTAs de Intercom ────────────────────────────────────
    // Este gating hace el rollout TRANSPARENTE: los call sites migran de
    // `window.Intercom('showNewMessage', text)` a `SupportChatService.showNewMessage(text)`.
    // - Si el user está en la allowlist (isEnabled) → abre nuestra burbuja con el texto.
    // - Si NO → cae a Intercom, con el mismo guard de siempre. Así un user fuera de la
    //   allowlist sigue yendo a Intercom sin que el call site sepa nada del rollout.
    showNewMessage(text) {
        if (_enabled) {
            this.openWithMessage(text);
            return;
        }
        if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
            window.Intercom('showNewMessage', text);
        }
    },

    // ── Drafts en localStorage ───────────────────────────────────────────────
    // Un reporte de bug largo no se pierde al cerrar el panel. key: conversationId (número/string)
    // o 'new' para la conversación que todavía no existe. Guardamos strings planos.
    // try/catch silencioso: localStorage puede fallar en incógnito / storage lleno.
    // La clave lleva el usuario: con una clave global, en un navegador compartido la cuenta siguiente
    // veía (y podía enviar) el borrador sin enviar de la anterior, aunque fuera de otra agencia
    // (C-fserb-d0-02). Además el logout los barre todos (`LocalStorage.clearSessionData`); la clave
    // por usuario cubre lo que no pasa por ahí (la sesión vencida, un cierre sin logout).
    _draftKey(key) {
        const userId = LocalStorage.fetchUserId() || 'anon';
        return `${DRAFT_KEY_PREFIX}${userId}_${key !== undefined && key !== null ? key : 'new'}`;
    },

    getDraft(key) {
        try {
            return localStorage.getItem(this._draftKey(key)) || '';
        } catch (_) {
            return '';
        }
    },

    saveDraft(key, text) {
        try {
            localStorage.setItem(this._draftKey(key), text !== undefined && text !== null ? text : '');
        } catch (_) {
            // localStorage no disponible: se pierde el draft, no rompemos.
        }
    },

    clearDraft(key) {
        try {
            localStorage.removeItem(this._draftKey(key));
        } catch (_) {
            // idem.
        }
    },
};
